// src/components/DriverHeader.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import dockyardImg from "../assets/dockyard.png";
import { logoutUser } from "../actions/AuthActions";
import LocationIndicator from "./LocationIndicator";
import GPSEnableNotification from "./GPSEnableNotification";
import { toast } from "react-toastify";
import { clearDashboardCache, clearAllCacheOnLogout } from "../utils/clearDashboardCache";

const DriverHeader = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading } = useSelector((state) => state.auth);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [time, setTime] = useState(() => new Date());

  // Optimized: useCallback for logout
  const handleLogout = useCallback(async () => {
    if (window.confirm("Are you sure you want to log out?")) {
      await clearAllCacheOnLogout("driver");
      dispatch(logoutUser());
      navigate("/login", { replace: true });
    }
  }, [dispatch, navigate]);

  // Optimized clock: requestAnimationFrame + cleanup
  useEffect(() => {
    let animationFrame;
    let lastTime = Date.now();

    const updateClock = () => {
      const now = Date.now();
      if (now - lastTime >= 1000) {
        setTime(new Date());
        lastTime = now;
      }
      animationFrame = requestAnimationFrame(updateClock);
    };

    animationFrame = requestAnimationFrame(updateClock);

    return () => {
      if (animationFrame) cancelAnimationFrame(animationFrame);
    };
  }, []);

  // Memoized time string
  const currentTime = useMemo(
    () =>
      time.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }),
    [time]
  );

  const navLinks = useMemo(
    () => [
      { name: "Dashboard", path: "/driver/dashboard" },
      { name: "Bookings", path: "/driver/scheduled-bookings" },
      { name: "My Trips", path: "/driver/my-trips" },
    ],
    []
  );

  const isActive = useCallback(
    (path) => {
      if (path.includes("?")) {
        return `${location.pathname}${location.search}` === path;
      }
      return location.pathname === path;
    },
    [location.pathname, location.search]
  );

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleClearCache = useCallback(async () => {
    const cleared = clearDashboardCache("driver");
    if ("caches" in window) {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n)));
    }
    toast.success(`Cache cleared (${cleared.length} item${cleared.length === 1 ? "" : "s"})`);
    setTimeout(() => window.location.reload(), 350);
  }, []);

  return (
    <header className="bg-gradient-to-r from-blue-800 to-blue-700 text-white shadow-md sticky top-0 z-50 safe-area-header">
      <div className="max-w-7xl mx-auto flex justify-between items-center px-4 sm:px-6 py-3 md:py-4">

        {/* Logo + Title + Clock */}
        <div className="flex items-center gap-4">
          <img
            src={dockyardImg}
            alt="Dockyard Logo"
            className="h-12 w-12 rounded-lg shadow-sm border border-blue-600 object-cover"
            loading="lazy"
          />
          <div className="flex flex-col sm:flex-row sm:items-center sm:gap-4">
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Aero View | Driver</h1>
            <div className="flex items-center gap-2 mt-1 sm:mt-0">
              <svg
                className="w-4 h-4 text-blue-200"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span
                className="px-3 py-1 bg-blue-700/30 rounded-md font-mono text-sm tracking-wide"
                aria-live="polite"
                aria-label={`Current time: ${currentTime}`}
              >
                {currentTime}
              </span>
              <div className="ml-3">
                <LocationIndicator />
              </div>
            </div>
          </div>
        </div>

        <GPSEnableNotification />

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-6" aria-label="Main navigation">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={link.path}
              className={`relative font-medium transition-all duration-200 px-1 focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:ring-offset-2 focus:ring-offset-blue-800 rounded ${
                isActive(link.path)
                  ? "text-yellow-300 after:absolute after:bottom-[-4px] after:left-0 after:w-full after:h-0.5 after:bg-yellow-300"
                  : "text-gray-100 hover:text-white"
              }`}
              aria-current={isActive(link.path) ? "page" : undefined}
            >
              {link.name}
            </Link>
          ))}

          <button
            onClick={handleClearCache}
            className="px-4 sm:px-5 py-2 rounded-lg font-medium bg-white/15 hover:bg-white/25 transition-all duration-200"
          >
            Clear Cache
          </button>

          <button
            onClick={handleLogout}
            disabled={loading}
            className={`px-4 sm:px-5 py-2 rounded-lg font-medium transition-all duration-200 flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2 focus:ring-offset-blue-800 ${
              loading
                ? "bg-red-700/70 cursor-not-allowed opacity-75"
                : "bg-red-600 hover:bg-red-700 shadow hover:shadow-md"
            }`}
            aria-label={loading ? "Logging out..." : "Logout"}
          >
            {loading ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                    opacity="0.3"
                  />
                  <path fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                <span className="sr-only">Logging out...</span>
                Logging out...
              </>
            ) : (
              "Logout"
            )}
          </button>
        </nav>

        {/* Mobile Menu Toggle */}
        <button
          className="md:hidden p-2 rounded-lg hover:bg-blue-700/40 transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:ring-offset-2 focus:ring-offset-blue-800"
          onClick={() => setMobileMenuOpen((prev) => !prev)}
          aria-label="Toggle navigation menu"
          aria-expanded={mobileMenuOpen}
          aria-controls="mobile-menu"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            {mobileMenuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <nav
          id="mobile-menu"
          className="md:hidden border-t border-blue-600/50 bg-blue-800/95 backdrop-blur-sm"
          aria-label="Mobile navigation"
        >
          <div className="px-4 py-3 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-3 rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:ring-inset ${
                  isActive(link.path)
                    ? "bg-blue-700 text-yellow-300"
                    : "text-gray-100 hover:bg-blue-700/80"
                }`}
                aria-current={isActive(link.path) ? "page" : undefined}
              >
                {link.name}
              </Link>
            ))}

            <button
              onClick={handleClearCache}
              className="w-full text-left px-4 py-3 rounded-md font-medium bg-white/10 hover:bg-white/20 text-white transition-colors"
            >
              Clear Cache
            </button>

            <button
              onClick={handleLogout}
              disabled={loading}
              className={`w-full text-left px-4 py-3 rounded-md font-medium flex items-center gap-2 transition-colors focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-inset ${
                loading
                  ? "bg-red-700/70 text-gray-300 opacity-75"
                  : "bg-red-600 hover:bg-red-700 text-white"
              }`}
              aria-label={loading ? "Logging out..." : "Logout"}
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                      opacity="0.3"
                    />
                    <path fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  <span className="sr-only">Logging out...</span>
                  Logging out...
                </>
              ) : (
                "Logout"
              )}
            </button>
          </div>
        </nav>
      )}
    </header>
  );
};

export default DriverHeader;
